import { useMemo, useState } from "react";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PUBLIC_DEALS_RETAILERS, type PublicDealsRetailer } from "../../config/publicDeals";
import { searchOnlineDeals, type GlobalSearchProduct, type GlobalSearchSort } from "../../services/GlobalSearchService";
import type { ProductListItem } from "../../types/Product";
import { getProductImageUrl } from "../../utils/imageUtils";
import { getGuestDealDiscount, getGuestDealPrice } from "../guest-deals/GuestDealsUi.utils";
import { HF, cleanName, retailerLabel } from "./homeFeedUi";
import { SalePrice } from "./HomePromos";
import ProductTileImage from "./ProductTileImage";

const PAGE_SIZE = 36;
const LANDING_FONT = `Outfit, ${HF.font}`;
const CARD_TILE = "var(--hf-card-tile)";
const DEAL_GREEN = "#0A7A3B";
const MUTED = HF.text3;
const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SORTS: Array<{ value: GlobalSearchSort; label: string }> = [
  { value: "relevance", label: "Best match" },
  { value: "discount", label: "Biggest % off" },
  { value: "priceAsc", label: "Lowest price" },
];

/** API retailer id → the public store it belongs to (online feeds only). */
const STORE_BY_API = new Map(PUBLIC_DEALS_RETAILERS.map((retailer) => [retailer.apiRetailer, retailer]));

/** Online deals from every store matching the header search, with store chips and a sort. */
export default function GlobalSearchResults({
  query,
  onOpenDeal,
  onClear,
}: {
  query: string;
  onOpenDeal: (product: ProductListItem, retailer: PublicDealsRetailer) => void;
  onClear: () => void;
}) {
  const [retailer, setRetailer] = useState("");
  const [sort, setSort] = useState<GlobalSearchSort>("relevance");
  // A new query starts over on every store.
  const [lastQuery, setLastQuery] = useState(query);
  if (lastQuery !== query) {
    setLastQuery(query);
    setRetailer("");
  }

  const search = useInfiniteQuery({
    queryKey: ["global-search", query, retailer, sort],
    initialPageParam: 0,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: ({ pageParam }) => searchOnlineDeals({ q: query, retailer, sort, limit: PAGE_SIZE, offset: pageParam }),
    getNextPageParam: (last, pages) => (last.hasMore ? pages.length * PAGE_SIZE : undefined),
  });
  const first = search.data?.pages[0];
  const items = useMemo(() => (search.data?.pages ?? []).flatMap((page) => page.products), [search.data]);
  const storeCounts = (first?.retailers ?? []).filter((entry) => STORE_BY_API.has(entry.retailer));
  const allCount = storeCounts.reduce((sum, entry) => sum + entry.count, 0);
  const loading = search.isPending || (search.isFetching && !search.isFetchingNextPage && search.isPlaceholderData);

  return (
    <div className="px-4 pb-28 pt-5 sm:px-8 sm:pt-8 lg:pb-12" style={{ fontFamily: LANDING_FONT, color: HF.ink }} data-testid="global-search">
      <div className="mx-auto flex max-w-[1320px] flex-col gap-5">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
          <div className="flex min-w-0 flex-col gap-1">
            <h1 className="m-0 text-[24px] font-bold tracking-[-0.5px] sm:text-[30px] sm:tracking-[-0.7px]">
              Online deals for “{query}”
            </h1>
            <span className="text-[14px] sm:text-[15px]" style={{ color: MUTED }}>
              {first && !first.ready
                ? "Search is warming up. Try again in a few seconds."
                : first
                  ? `${allCount.toLocaleString()} ${allCount === 1 ? "deal" : "deals"} across ${storeCounts.length} ${storeCounts.length === 1 ? "store" : "stores"}`
                  : "Searching every store…"}
            </span>
          </div>
          <span className="flex-1" />
          <label className="flex items-center gap-2 text-[14px]" style={{ color: MUTED }}>
            Sort
            <select
              value={sort}
              onChange={(event) => setSort(event.target.value as GlobalSearchSort)}
              className="h-10 cursor-pointer rounded-full border px-3.5 text-[14px] font-semibold outline-none"
              style={{ borderColor: HF.chipBorder, background: HF.surface, color: HF.ink }}
            >
              {SORTS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>

        {storeCounts.length > 1 && (
          <nav aria-label="Filter by store" className="scrollbar-hide -mx-4 flex gap-2 overflow-x-auto px-4 sm:mx-0 sm:flex-wrap sm:px-0">
            <StoreChip label="All stores" count={allCount} active={retailer === ""} onClick={() => setRetailer("")} />
            {storeCounts.map((entry) => (
              <StoreChip
                key={entry.retailer}
                label={retailerLabel(STORE_BY_API.get(entry.retailer)!)}
                count={entry.count}
                active={retailer === entry.retailer}
                onClick={() => setRetailer(retailer === entry.retailer ? "" : entry.retailer)}
              />
            ))}
          </nav>
        )}

        {search.isError ? (
          <EmptyState title="Search didn’t load" text="Check your connection and try again." onClear={onClear} retry={() => void search.refetch()} />
        ) : loading && items.length === 0 ? (
          <div className="grid grid-cols-2 gap-x-3.5 gap-y-6 sm:grid-cols-3 sm:gap-x-[22px] lg:grid-cols-4 xl:grid-cols-6" aria-busy="true">
            {Array.from({ length: 12 }, (_, i) => <div key={i} className="aspect-square animate-pulse rounded-[16px]" style={{ background: HF.skeleton }} />)}
          </div>
        ) : items.length === 0 && first?.ready !== false ? (
          <EmptyState title={`No online deals match “${query}”`} text="Try fewer words or a brand name, like “KitchenAid” or “Crocs”." onClear={onClear} />
        ) : (
          <div className={`grid grid-cols-2 gap-x-3.5 gap-y-6 sm:grid-cols-3 sm:gap-x-[22px] lg:grid-cols-4 xl:grid-cols-6 ${loading ? "opacity-60" : ""}`}>
            {items.map((item, index) => (
              <SearchDealCard key={`${item.productKey}-${index}`} item={item} onOpen={onOpenDeal} />
            ))}
          </div>
        )}

        {search.hasNextPage && (
          <button
            type="button"
            onClick={() => void search.fetchNextPage()}
            disabled={search.isFetchingNextPage}
            className="mx-auto mt-2 flex h-12 items-center rounded-full border px-7 text-[15px] font-semibold disabled:opacity-60"
            style={{ borderColor: HF.chipBorder, color: HF.ink }}
          >
            {search.isFetchingNextPage ? "Loading…" : "Show more deals"}
          </button>
        )}
      </div>
    </div>
  );
}

function StoreChip({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="flex h-9 flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-[14px] font-medium transition-colors"
      style={active ? { background: "#3A0A63", color: "#FFFFFF" } : { background: HF.surface2, color: HF.ink }}
    >
      {label}
      <span className="tabular-nums" style={{ opacity: 0.7 }}>{count.toLocaleString()}</span>
    </button>
  );
}

function SearchDealCard({ item, onOpen }: { item: GlobalSearchProduct; onOpen: (product: ProductListItem, retailer: PublicDealsRetailer) => void }) {
  const store = STORE_BY_API.get(item.retailer);
  if (!store) return null;
  const price = getGuestDealPrice(item);
  const msrp = Number(item.msrp) || 0;
  const off = getGuestDealDiscount(item);
  const onSale = msrp > price && price > 0;
  return (
    <button type="button" onClick={() => onOpen(item, store)} className="group flex min-w-0 flex-col gap-1 text-left" data-testid="global-search-card">
      <span className="relative block aspect-square w-full overflow-hidden rounded-[16px]" style={{ background: CARD_TILE }}>
        {item.image && <ProductTileImage src={getProductImageUrl(item.image, "400")} className="mix-blend-multiply transition-transform duration-200 group-hover:scale-[1.03]" />}
        {off > 0 && (
          <span className="absolute left-2 top-2 rounded-full px-2 py-0.5 text-[12px] font-bold text-white sm:left-2.5 sm:top-2.5 sm:text-[13px]" style={{ background: DEAL_GREEN }}>{off}% off</span>
        )}
      </span>
      <span className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        {price > 0 ? <SalePrice value={price} /> : <span className="text-[16px] font-bold">See price</span>}
        {onSale && <span className="text-[13px] line-through sm:text-[14px]" style={{ color: MUTED }}>{money.format(msrp)}</span>}
      </span>
      <span className="line-clamp-2 text-[14px] leading-[1.3] sm:text-[15px]">{cleanName(item.name)}</span>
      <span className="text-[12.5px] sm:text-[13px]" style={{ color: MUTED }}>{retailerLabel(store)}</span>
    </button>
  );
}

function EmptyState({ title, text, onClear, retry }: { title: string; text: string; onClear: () => void; retry?: () => void }) {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-2 py-14 text-center" role="status">
      <p className="m-0 text-[18px] font-bold">{title}</p>
      <p className="m-0 text-[15px]" style={{ color: MUTED }}>{text}</p>
      <div className="mt-3 flex gap-2">
        {retry && (
          <button type="button" onClick={retry} className="h-11 rounded-full px-5 text-[15px] font-bold text-white" style={{ background: "#3A0A63" }}>Try again</button>
        )}
        <button type="button" onClick={onClear} className="h-11 rounded-full border px-5 text-[15px] font-semibold" style={{ borderColor: HF.chipBorder, color: HF.ink }}>Back to home</button>
        <Link to="/stores" className="flex h-11 items-center rounded-full px-4 text-[15px] font-semibold no-underline" style={{ color: HF.brandText }}>Browse stores</Link>
      </div>
    </div>
  );
}
